import * as React from 'react';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import ReadMoreIcon from '@mui/icons-material/ReadMore';
import useRaf from '@dh-react-hooks/use-raf';

/**
 * A wrapper component that displays a Sidebar and the main content.
 *
 * @param {DrawerWrapperProps} props - The props for the component.
 * @returns {React.ReactElement} The rendered JSX.
 */
export default function DrawerWrapper(
    props: DrawerWrapperProps
): React.ReactElement {
    const {
        Sidebar,
        innerSidebarRef,
        children,
        setData,
        setTotalRows,
        page,
        rowsPerPage,
        setPage,
        setIsLoading,
        isLoading,
    } = props;
    const DrawerTransitionDuration = 300;
    // const innerRef = React.useRef<inferFilteredSidebar | null>(null);
    const [isDrawerOpen, setDrawer] = React.useState<boolean>(true);
    const [sideBarWidth, setSideBarWidth] = React.useState<number>(260);
    const RafCallback = React.useCallback(
        (number: number) => {
            if (isDrawerOpen) {
                setSideBarWidth(260);
            } else {
                const px = 260 - 260 * (number / DrawerTransitionDuration);
                if (px > 0) {
                    setSideBarWidth(px);
                } else {
                    setSideBarWidth(0);
                }
            }
        },
        [isDrawerOpen]
    );
    const { start } = useRaf({
        disable: true,
        duration: DrawerTransitionDuration,
        callback: RafCallback,
    });

    const HandleDrawerPersistent = React.useCallback(() => {
        setDrawer(!isDrawerOpen);
        start();
    }, [isDrawerOpen, start]);

    return (
        <Box className="sidebar-wrapper managepacket-container">
            <CssBaseline />
            <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={HandleDrawerPersistent}
                edge="start"
                className="drawer-toggler"
            >
                {isDrawerOpen ? (
                    <LowPriorityIcon className="rotate-priority" />
                ) : (
                    <ReadMoreIcon />
                )}
            </IconButton>
            <Box
                component="nav"
                aria-label="search and filters folders"
                className="sidebar-widget"
                sx={{
                    '.side-menu-filter-and-sorting': {
                        width: `${sideBarWidth}px !important`,
                    },
                }}
            >
                <Drawer
                    id="drawerForWrapper"
                    variant="persistent"
                    anchor="left"
                    transitionDuration={{
                        enter: 0,
                        exit: DrawerTransitionDuration,
                    }}
                    className={isDrawerOpen ? 'opened' : 'closed'}
                    open={isDrawerOpen}
                >
                    <Sidebar
                        ref={innerSidebarRef}
                        setData={setData}
                        setTotalRows={setTotalRows}
                        page={page}
                        setPage={setPage}
                        setIsLoading={setIsLoading}
                        rowsPerPage={rowsPerPage}
                        isLoading={isLoading}
                    />
                </Drawer>
            </Box>
            <Box component="main" className="sidebar-container">
                {children}
            </Box>
        </Box>
    );
}
